/**
 * Chinese Lunar Calendar utility
 * 使用 lunar-typescript（6tail/lunar TS 版，与后端 Tyme4j 同源）进行精确农历换算，
 * 覆盖任意年份，不再受 2020-2030 数据表限制。
 * Year stem/branch (年干支) is determined by 立春 (Start of Spring), not lunar new year.
 */
import { Solar, Lunar, LunarMonth, LunarYear } from 'lunar-typescript';



/**
 * 将农历日期转换为公历日期
 * 支持任意年份精确计算（lunar-typescript）
 * 闰月输入时 isLeapMonth=true
 */
export function lunarToSolar(
  lunarYear: number,
  lunarMonth: number,
  lunarDay: number,
  isLeapMonth: boolean,
): { year: number; month: number; day: number } | null {
  try {
    const lunar = Lunar.fromYmd(lunarYear, isLeapMonth ? -lunarMonth : lunarMonth, lunarDay);
    const solar = lunar.getSolar();
    return {
      year: solar.getYear(),
      month: solar.getMonth(),
      day: solar.getDay(),
    };
  } catch {
    return null;
  }
}

// ─── 公开接口 ─────────────────────────────────────────────────────────────────

export interface LunarDate {
  lunarYear: number;    // 农历年（以正月初一换年）
  lunarMonth: number;   // 农历月（1-12）
  lunarDay: number;     // 农历日（1-30）
  isLeapMonth: boolean; // 是否闰月
  ganzhiYear: number;   // 干支年（以立春换年，用于年柱计算）
}

/**
 * 将公历日期转换为农历日期
 * 支持任意年份精确计算（lunar-typescript）
 */
export function solarToLunar(date: Date): LunarDate {
  const solar = Solar.fromYmd(date.getFullYear(), date.getMonth() + 1, date.getDate());
  const lunar = solar.getLunar();

  const lunarYear = lunar.getYear();
  const rawMonth = lunar.getMonth();
  const isLeapMonth = rawMonth < 0;
  const lunarMonth = Math.abs(rawMonth);
  const lunarDay = lunar.getDay();

  // 干支年（以立春换年）：由 lunar 精确干支索引反推"干支年数字"，兼容 (ganzhiYear-4)%10/%12
  const ganZhiYear = calcGanZhiYearNum(
    lunar.getYearGanIndexExact(),
    lunar.getYearZhiIndexExact(),
  );

  return {
    lunarYear,
    lunarMonth,
    lunarDay,
    isLeapMonth,
    ganzhiYear: ganZhiYear,
  };
}

/**
 * 由天干/地支索引反推"干支年数字"，使 (num-4)%10=stemIdx 且 (num-4)%12=branchIdx
 */
function calcGanZhiYearNum(stemIdx: number, branchIdx: number): number {
  // num = 4 + stemIdx + 10k，需 (4 + stemIdx + 10k - 4) % 12 === branchIdx
  // => (stemIdx + 10k) % 12 === branchIdx => (stemIdx + 10k) mod 12
  const k = (branchIdx - stemIdx + 12) % 12;
  // stemIdx + 10k 需要 ≡ branchIdx (mod 12)。10 ≡ -2 (mod 12)，所以 stemIdx - 2k ≡ branchIdx
  // => 2k ≡ stemIdx - branchIdx (mod 12) => k ≡ (stemIdx - branchIdx) * 6 (mod 12)，但 6 不可逆。
  // 改用直接枚举：找一个 year 满足条件
  for (let y = 4; y < 4 + 120; y++) {
    if (((y - 4) % 10 + 10) % 10 === stemIdx && ((y - 4) % 12 + 12) % 12 === branchIdx) {
      return y;
    }
  }
  // 兜底（正常不会到达）：返回以 stemIdx 为基础的值
  return 4 + stemIdx;
}

// ─── 兼容旧接口的辅助函数 ─────────────────────────────────────────────────────

/**
 * 获取某农历年某月的天数（29或30）
 */
export function getLunarMonthDays(year: number, month: number): number {
  try {
    return LunarMonth.fromYm(year, month).getDayCount();
  } catch {
    return 29;
  }
}

/**
 * 获取某农历年的闰月月份（0=无闰月）
 */
export function getLunarLeapMonth(year: number): number {
  try {
    return LunarYear.fromYear(year).getLeapMonth();
  } catch {
    return 0;
  }
}

/**
 * 获取某农历年闰月的天数
 */
export function getLunarLeapMonthDays(year: number): number {
  try {
    const leap = LunarYear.fromYear(year).getLeapMonth();
    if (leap <= 0) return 29;
    return LunarMonth.fromYm(year, -leap).getDayCount();
  } catch {
    return 29;
  }
}

/**
 * 获取某农历年的总月数（12或13）
 */
export function getLunarMonthsCount(year: number): number {
  try {
    return LunarYear.fromYear(year).getMonths().length;
  } catch {
    return 12;
  }
}

/**
 * 获取公历某月的天数
 */
export function getSolarMonthDays(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

// ─── 生肖与农历字符串辅助（新增）─────────────────────────────────────────────

const ZODIAC_ANIMALS = ['鼠', '牛', '虎', '兔', '龙', '蛇', '马', '羊', '猴', '鸡', '狗', '猪'] as const;
const LUNAR_STEMS = ['甲', '乙', '丙', '丁', '戊', '己', '庚', '辛', '壬', '癸'] as const;
const LUNAR_BRANCHES = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥'] as const;

const LUNAR_MONTH_NAMES = ['正月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '腊月'];
const LUNAR_DAY_NAMES = [
  '初一', '初二', '初三', '初四', '初五', '初六', '初七', '初八', '初九', '初十',
  '十一', '十二', '十三', '十四', '十五', '十六', '十七', '十八', '十九', '二十',
  '廿一', '廿二', '廿三', '廿四', '廿五', '廿六', '廿七', '廿八', '廿九', '三十',
];
const SHICHEN_NAMES = ['子时', '丑时', '寅时', '卯时', '辰时', '巳时', '午时', '未时', '申时', '酉时', '戌时', '亥时'];

/**
 * 根据公历日期精确计算生肖（以农历正月初一换年）
 * 支持 2020-2030 精确数据，超出范围使用保守近似
 */
export function getZodiacFromSolarDate(date: Date): string {
  try {
    // 按立春精确切年（与排盘年柱一致）
    const lunar = Solar.fromYmd(date.getFullYear(), date.getMonth() + 1, date.getDate()).getLunar();
    return lunar.getYearShengXiaoExact();
  } catch {
    return ZODIAC_ANIMALS[((date.getFullYear() - 4) % 12 + 12) % 12];
  }
}

/**
 * 根据公历日期和小时生成农历字符串（如"壬寅年五月廿三午时"）
 * 时辰末尾使用繁体"時"以兼容 convertLunarDateI18n 的多语言转换
 */
export function buildLunarString(date: Date, hour: number): string {
  const lunar = solarToLunar(date);

  // 年干支（ganzhiYear 已由 solarToLunar 按立春计算好）
  const yearStemIdx = ((lunar.ganzhiYear - 4) % 10 + 10) % 10;
  const yearBranchIdx = ((lunar.ganzhiYear - 4) % 12 + 12) % 12;
  const yearGanZhi = LUNAR_STEMS[yearStemIdx] + LUNAR_BRANCHES[yearBranchIdx];

  // 农历月份
  const monthName = LUNAR_MONTH_NAMES[lunar.lunarMonth - 1] || '';
  const leapPrefix = lunar.isLeapMonth ? '闰' : '';

  // 农历日期
  const dayName = LUNAR_DAY_NAMES[lunar.lunarDay - 1] || '';

  // 时辰（使用繁体"時"以兼容 i18n 转换）
  const shichenIdx = Math.floor(((hour + 1) % 24) / 2);
  const shichenName = (SHICHEN_NAMES[shichenIdx] || '子时').replace('时', '時');

  return `${yearGanZhi}年${leapPrefix}${monthName}${dayName}${shichenName}`;
}

// ─── 四柱（干支）辅助函数 ─────────────────────────────────────────────────────

/**
 * 计算日柱在60甲子中的位置（0-59）
 * 参考点：1900-01-01 = 甲戌日（位置10）
 * 验证：甲=天干0，戌=地支10，60甲子序号=10
 * 与 baziCalc.ts calcDayPillar 使用相同参考点保持一致
 */
function getDayPos60(year: number, month: number, day: number): number {
  const REF_POS = 10; // 甲戌 = 位置10（1900-01-01）
  const refDate = new Date(1900, 0, 1);
  const targetDate = new Date(year, month - 1, day);
  const diffDays = Math.round((targetDate.getTime() - refDate.getTime()) / 86400000);
  return ((REF_POS + diffDays) % 60 + 60) % 60;
}

/**
 * 返回某日的日柱 [天干索引(0-9), 地支索引(0-11)]
 */
export function getDayPillar(year: number, month: number, day: number): [number, number] {
  const pos = getDayPos60(year, month, day);
  return [pos % 10, pos % 12];
}

/**
 * 五虎遁年起月法：根据年干索引和月支索引，返回月干索引
 */
export function getMonthStem(yearStemIdx: number, monthBranchIdx: number): number {
  const monthIndex = (monthBranchIdx - 2 + 12) % 12;
  const startStems = [2, 4, 6, 8, 0]; // 丙戊庚壬甲
  return (startStems[yearStemIdx % 5] + monthIndex) % 10;
}

/**
 * 五鼠遁日起时法：根据日干索引和时支索引，返回时干索引
 */
export function getTimeStem(dayStemIdx: number, timeBranchIdx: number): number {
  const startStems = [0, 2, 4, 6, 8]; // 甲丙戊庚壬
  return (startStems[dayStemIdx % 5] + timeBranchIdx) % 10;
}

/**
 * 获取干支组合在60甲子中的位置
 * 如果阴阳不匹配返回 -1
 */
export function getPillarPos(stemIdx: number, branchIdx: number): number {
  if (stemIdx < 0 || branchIdx < 0) return -1;
  if (stemIdx % 2 !== branchIdx % 2) return -1;
  const halfDiff = (branchIdx - stemIdx) / 2;
  const k = ((halfDiff * 5) % 6 + 6) % 6;
  return (stemIdx + 10 * k) % 60;
}

/**
 * 返回1801-2099年中与给定年柱相同的所有年份
 */
export function getYearsWithPillar(stemIdx: number, branchIdx: number): number[] {
  if (stemIdx < 0 || branchIdx < 0) return [];
  if (stemIdx % 2 !== branchIdx % 2) return [];
  const years: number[] = [];
  for (let y = 1801; y <= 2099; y++) {
    const s = ((y - 4) % 10 + 10) % 10;
    const b = ((y - 4) % 12 + 12) % 12;
    if (s === stemIdx && b === branchIdx) years.push(y);
  }
  return years;
}

/**
 * 根据公历年返回该年的年柱 [天干索引, 地支索引]
 * 注意：这里用公历年近似，精确计算需考虑立春
 */
export function getYearPillar(year: number): [number, number] {
  const s = ((year - 4) % 10 + 10) % 10;
  const b = ((year - 4) % 12 + 12) % 12;
  return [s, b];
}

/**
 * 根据公历月份返回月支索引
 * 月1 → 寅(2), 月2 → 卯(3), ..., 月11 → 子(0), 月12 → 丑(1)
 */
export function getMonthBranchFromSolarMonth(solarMonth: number): number {
  return (solarMonth + 1) % 12;
}

/**
 * 根据小时返回时支索引
 * 子时: 23-1 → 0, 丑时: 1-3 → 1, ...
 */
export function getTimeBranchFromHour(hour: number): number {
  return Math.floor(((hour + 1) % 24) / 2);
}
